import React from "react";
import { useTranslation } from "react-i18next";
import { useSelector } from "react-redux";
import { DropResult } from "react-beautiful-dnd";

import { showErrorToast } from "@components/Common/ErrorToast";
import { RootState } from "../../../../../../redux/store";
import { ScanToContext } from "..";
import {
    ScanToMobilePreview,
    OffresPreviewData,
    ActionsPreviewData,
    PublicitesPreviewData,
    CommandePreviewData,
    DiversPreviewData,
} from "../View";

import { PropsType, ImageItem } from "./types";
import {
    DEFAULT_TRANSLATIONS,
    LANGUAGE_CODES,
    ACTIVITY_BANNER_IMAGES,
} from "./constants";
import {
    DISPLAY_TYPE_MOBILE,
    DISPLAY_KEY_MOBILE,
    DISPLAY_KEY_DESKTOP,
    SCAN_TO_DEFAULT_MAIN_COLOR,
} from "../shared/constants";
import {
    Container,
    LeftSection,
    RightSection,
    SectionHeader,
    PreviewToggleButton,
    ClosePreviewButton,
    OpenInNewWindowButton,
    PreviewOverlay,
    SectionTitle,
    ToggleContainer,
    ToggleLabel,
    Toggle,
} from "./styled";
import { ImageTable } from "./ImageTable";
import { TitleDescription } from "./TitleDescription";
import { DeleteConfirmModal } from "./DeleteConfirmModal";

export function Banniere({ rowData }: PropsType): JSX.Element {
    const { t } = useTranslation();
    const mainColor =
        useSelector((state: RootState) => state.global.infoUser.maincolor) ||
        SCAN_TO_DEFAULT_MAIN_COLOR;

    const {
        selectedActivity,
        sharedOffresData,
        sharedActionsData,
        sharedPublicitesData,
        sharedCommandeData,
        sharedDiversData,
        sharedPaiementData,
        sharedAppelServeurData,
        setSharedBannerData,
        registerPendingFile,
        removePendingFile,
        registerDeletedImage,
    } = React.useContext(ScanToContext);

    // Cast: the slice's BannerConfig type doesn't declare every UI-state field
    // that lives at runtime (selectedLanguage, etc.). Out of scope for ARCH-02.
    const savedBanner = useSelector(
        (state: RootState) => state.scanTo?.banner
    ) as any;
    const [bannerEnabled, setBannerEnabled] = React.useState(
        savedBanner?.bannerEnabled ?? true
    );
    const [selectedLanguage, setSelectedLanguage] = React.useState(
        savedBanner?.selectedLanguage || "fr"
    );
    const [currentSlide, setCurrentSlide] = React.useState(0);
    const [displayType, setDisplayType] = React.useState(
        savedBanner?.displayType || "desktop"
    );
    const [images, setImages] = React.useState<{
        Mobile: ImageItem[];
        Desktop: ImageItem[];
    }>(() => {
        const raw = savedBanner?.images;
        // Handle old flat-array format → migrate to new { Mobile, Desktop } structure
        if (Array.isArray(raw)) return { Mobile: [], Desktop: raw };
        if (raw && raw.Mobile !== undefined) return raw;
        return { Mobile: [], Desktop: [] };
    });
    // Computed: current images list based on displayType
    const displayKey: keyof typeof images =
        displayType === DISPLAY_TYPE_MOBILE
            ? DISPLAY_KEY_MOBILE
            : DISPLAY_KEY_DESKTOP;
    const currentImages = images[displayKey];
    const setCurrentImages = (updated: ImageItem[]) => {
        setImages({ ...images, [displayKey]: updated });
    };

    const [deleteConfirmOpen, setDeleteConfirmOpen] = React.useState(false);
    const [imageToDelete, setImageToDelete] = React.useState<number | null>(
        null
    );
    const fileInputRef = React.useRef<HTMLInputElement>(null);
    const [uploadedImages, setUploadedImages] = React.useState<
        Record<number, string>
    >({});

    // Visibility states
    const [titleVisible, setTitleVisible] = React.useState(
        savedBanner?.titleVisible ?? true
    );
    const [descriptionVisible, setDescriptionVisible] = React.useState(
        savedBanner?.descriptionVisible ?? true
    );
    const [previewVisible, setPreviewVisible] = React.useState(true);

    // Color states for title and description
    const [titleColor, setTitleColor] = React.useState(
        savedBanner?.titleColor || "#ffffff"
    );
    const [descriptionColor, setDescriptionColor] = React.useState(
        savedBanner?.descriptionColor || "#ffffff"
    );

    // offresData and actionsData come from context (sharedOffresData, sharedActionsData)

    // Default translations for each language
    const defaultTranslations = DEFAULT_TRANSLATIONS;

    // Custom texts per language
    const [customTexts, setCustomTexts] = React.useState<
        Record<string, { title: string; description: string }>
    >(
        savedBanner?.customTexts || {
            fr: {
                title: defaultTranslations.fr.title,
                description: defaultTranslations.fr.description,
            },
            en: {
                title: defaultTranslations.en.title,
                description: defaultTranslations.en.description,
            },
            ru: {
                title: defaultTranslations.ru.title,
                description: defaultTranslations.ru.description,
            },
            it: {
                title: defaultTranslations.it.title,
                description: defaultTranslations.it.description,
            },
            es: {
                title: defaultTranslations.es.title,
                description: defaultTranslations.es.description,
            },
            pt: {
                title: defaultTranslations.pt.title,
                description: defaultTranslations.pt.description,
            },
            de: {
                title: defaultTranslations.de.title,
                description: defaultTranslations.de.description,
            },
            ar: {
                title: defaultTranslations.ar.title,
                description: defaultTranslations.ar.description,
            },
        }
    );

    const languages = React.useMemo(
        () =>
            LANGUAGE_CODES.map((l) => ({ code: l.code, label: t(l.labelKey) })),
        [t]
    );

    // Banner images for each activity (2 images per activity) — local assets
    const activityBannerImages = ACTIVITY_BANNER_IMAGES;

    // Handle drag and drop
    const handleDragEnd = (result: DropResult) => {
        if (!result.destination) return;

        const sourceIndex = result.source.index;
        const destinationIndex = result.destination.index;

        if (sourceIndex === destinationIndex) return;

        const reorderedImages = Array.from(currentImages);
        const [movedImage] = reorderedImages.splice(sourceIndex, 1);
        reorderedImages.splice(destinationIndex, 0, movedImage);

        // Update order values
        const updatedImages = reorderedImages.map((img, index) => ({
            ...img,
            order: index + 1,
        }));

        setCurrentImages(updatedImages);
    };

    // Handle manual order change
    const handleOrderChange = (imageId: number, newOrder: string) => {
        const orderNum = parseInt(newOrder, 10);

        if (isNaN(orderNum) || orderNum < 1 || orderNum > currentImages.length)
            return;

        const currentImage = currentImages.find((img) => img.id === imageId);
        if (!currentImage) return;

        const currentIndex = currentImages.findIndex(
            (img) => img.id === imageId
        );
        const newIndex = orderNum - 1;

        if (currentIndex === newIndex) return;

        const reorderedImages = Array.from(currentImages);
        reorderedImages.splice(currentIndex, 1);
        reorderedImages.splice(newIndex, 0, currentImage);

        // Update order values
        const updatedImages = reorderedImages.map((img, index) => ({
            ...img,
            order: index + 1,
        }));

        setCurrentImages(updatedImages);
    };

    // Toggle individual image activation
    const toggleImageActive = (imageId: number) => {
        setCurrentImages(
            currentImages.map((img) =>
                img.id === imageId ? { ...img, active: !img.active } : img
            )
        );
    };

    // Handle view image
    const handleViewImage = (imageId: number) => {
        const image = currentImages.find((img) => img.id === imageId);
        if (image) {
            const imgIdx = currentImages
                .filter((i) => i.activityId === image.activityId)
                .findIndex((i) => i.id === image.id);
            const activityDefaults =
                activityBannerImages[image.activityId] || [];
            const url =
                uploadedImages[imageId] ||
                image.url ||
                (image.filename?.startsWith("http") ? image.filename : "") ||
                activityDefaults[imgIdx] ||
                activityDefaults[0] ||
                "";
            if (url) window.open(url, "_blank");
        }
    };

    // Handle delete confirmation
    const handleDeleteClick = (imageId: number) => {
        setImageToDelete(imageId);
        setDeleteConfirmOpen(true);
    };

    const confirmDelete = () => {
        if (imageToDelete !== null) {
            const img = currentImages.find((i) => i.id === imageToDelete);
            // Track server image for deletion
            if (img?.filename) registerDeletedImage(img.filename);

            // Revoke the blob URL if it's an uploaded image to prevent memory leaks
            if (uploadedImages[imageToDelete]) {
                removePendingFile(uploadedImages[imageToDelete]);
                URL.revokeObjectURL(uploadedImages[imageToDelete]);
                const newUploadedImages = { ...uploadedImages };
                delete newUploadedImages[imageToDelete];
                setUploadedImages(newUploadedImages);
            }

            setCurrentImages(
                currentImages.filter((img) => img.id !== imageToDelete)
            );
            setDeleteConfirmOpen(false);
            setImageToDelete(null);
        }
    };

    const cancelDelete = () => {
        setDeleteConfirmOpen(false);
        setImageToDelete(null);
    };

    // Handle image upload
    const handleAddButtonClick = () => {
        fileInputRef.current?.click();
    };

    const handleImageUpload = (event: React.ChangeEvent<HTMLInputElement>) => {
        const file = event.target.files?.[0];
        if (!file) return;

        // Validate file type
        const validTypes = ["image/png", "image/jpeg", "image/jpg"];
        if (!validTypes.includes(file.type)) {
            showErrorToast(
                t("Invalid file format. Please use PNG, JPG or JPEG.")
            );
            return;
        }

        // Validate file size (max 5MB)
        const maxSize = 5 * 1024 * 1024; // 5MB in bytes
        if (file.size > maxSize) {
            showErrorToast(t("File size is too large. Maximum 5MB."));
            return;
        }

        // Create a URL for the uploaded image
        const imageUrl = URL.createObjectURL(file);
        registerPendingFile(imageUrl, file);

        // Get activity label from activity ID
        const activityLabels: Record<string, string> = {
            fastfood: "Fastfood",
            traditional_restaurant: "Traditional restaurant",
            cafe: "Café",
            hotel: "Hotel",
            camping: "Camping",
        };

        // Create new image item — use IDs from both lists to avoid collisions
        const allIds = [...images.Mobile, ...images.Desktop].map(
            (img) => img.id
        );
        const newImageId = Math.max(...allIds, 0) + 1;
        const newImage: ImageItem = {
            id: newImageId,
            order: currentImages.length + 1,
            filename: file.name,
            url: imageUrl,
            type: "image",
            active: true,
            activityId: selectedActivity,
            activityLabel: activityLabels[selectedActivity] || "Fastfood",
        };

        // Add to current display type's images array
        setCurrentImages([...currentImages, newImage]);

        // Store the uploaded image URL
        setUploadedImages({
            ...uploadedImages,
            [newImageId]: imageUrl,
        });

        // Reset file input
        if (fileInputRef.current) {
            fileInputRef.current.value = "";
        }
    };

    // Handle custom text changes
    const handleCustomTitleChange = (value: string) => {
        setCustomTexts({
            ...customTexts,
            [selectedLanguage]: {
                ...customTexts[selectedLanguage],
                title: value,
            },
        });
    };

    const handleCustomDescriptionChange = (value: string) => {
        setCustomTexts({
            ...customTexts,
            [selectedLanguage]: {
                ...customTexts[selectedLanguage],
                description: value,
            },
        });
    };

    // Reset to default translation
    const resetTitle = () => {
        setCustomTexts({
            ...customTexts,
            [selectedLanguage]: {
                ...customTexts[selectedLanguage],
                title: defaultTranslations[selectedLanguage].title,
            },
        });
        setTitleColor("#ffffff");
    };

    const resetDescription = () => {
        setCustomTexts({
            ...customTexts,
            [selectedLanguage]: {
                ...customTexts[selectedLanguage],
                description: defaultTranslations[selectedLanguage].description,
            },
        });
        setDescriptionColor("#ffffff");
    };

    // Reset all (title and description) for current language
    const resetAll = () => {
        setCustomTexts({
            ...customTexts,
            [selectedLanguage]: {
                title: defaultTranslations[selectedLanguage].title,
                description: defaultTranslations[selectedLanguage].description,
            },
        });
        setTitleColor("#ffffff");
        setDescriptionColor("#ffffff");
    };

    // Get all active images with their URLs (from current display type)
    // Priority: 1) blob URL → 2) server URL → 3) filename if http → 4) activity default
    const activeImages = currentImages
        .filter((img) => img.active)
        .map((img, idx) => {
            const activityDefaults = activityBannerImages[img.activityId] || [];
            return {
                ...img,
                url:
                    uploadedImages[img.id] ||
                    img.url ||
                    (img.filename?.startsWith("http") ? img.filename : "") ||
                    activityDefaults[idx] ||
                    activityDefaults[0] ||
                    "",
            };
        });

    // Reset slide index when active images change
    React.useEffect(() => {
        if (currentSlide >= activeImages.length) {
            setCurrentSlide(Math.max(0, activeImages.length - 1));
        }
    }, [activeImages.length, currentSlide]);

    // Register default activity images as pending files for upload
    React.useEffect(() => {
        currentImages.forEach((img) => {
            // Skip if already has a real URL (uploaded or from server)
            if (
                uploadedImages[img.id] ||
                img.url ||
                img.filename?.startsWith("http")
            )
                return;

            const activityDefaults = activityBannerImages[img.activityId] || [];
            const actIdx = currentImages
                .filter((i) => i.activityId === img.activityId)
                .findIndex((i) => i.id === img.id);
            const defaultSrc = activityDefaults[actIdx] || activityDefaults[0];
            if (!defaultSrc) return;

            fetch(defaultSrc)
                .then((res) => res.blob())
                .then((blob) => {
                    const file = new File(
                        [blob],
                        img.filename ||
                            `banner_${img.activityId}_${actIdx + 1}.jpg`,
                        { type: blob.type }
                    );
                    const blobUrl = URL.createObjectURL(blob);
                    registerPendingFile(blobUrl, file);
                    setUploadedImages((prev) => ({
                        ...prev,
                        [img.id]: blobUrl,
                    }));
                })
                .catch(() => {});
        });
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    // Cleanup blob URLs on component unmount.
    // Track the latest map in a ref so the cleanup effect reads it at unmount
    // without re-running (and revoking still-active URLs) on every change to
    // `uploadedImages`. Mirrors Publicites/index.tsx (currentAdsRef).
    const uploadedImagesRef = React.useRef(uploadedImages);
    React.useEffect(() => {
        uploadedImagesRef.current = uploadedImages;
    }, [uploadedImages]);

    React.useEffect(() => {
        return () => {
            Object.values(uploadedImagesRef.current).forEach((url) => {
                if (url.startsWith("blob:")) {
                    URL.revokeObjectURL(url);
                }
            });
        };
    }, []);

    // Broadcast channel for real-time updates to preview window
    const [
        broadcastChannel,
        setBroadcastChannel,
    ] = React.useState<BroadcastChannel | null>(null);

    // Initialize broadcast channel (for external preview window only)
    React.useEffect(() => {
        const channel = new BroadcastChannel("scan-to-preview-channel");
        setBroadcastChannel(channel);
        return () => {
            channel.close();
        };
    }, []);

    // Single source of truth for the banner preview payload — both the
    // auto-sync effect and the "open in new window" handler ship this exact
    // shape, so they cannot drift (the popup used to receive a truncated
    // subset missing `images` and `displayType`).
    const previewData = React.useMemo(
        () => ({
            mainColor,
            selectedLanguage,
            bannerEnabled,
            customTexts,
            activeImages: activeImages.map((img) => ({
                id: img.id,
                url: img.url,
                activityId: img.activityId,
                activityLabel: img.activityLabel,
            })),
            titleVisible,
            descriptionVisible,
            titleColor,
            descriptionColor,
            currentSlide,
            // Full config data for Redux persistence
            images,
            displayType,
        }),
        [
            mainColor,
            selectedLanguage,
            bannerEnabled,
            customTexts,
            activeImages,
            titleVisible,
            descriptionVisible,
            titleColor,
            descriptionColor,
            currentSlide,
            images,
            displayType,
        ]
    );

    // Share banner preview data via context + BroadcastChannel + sessionStorage
    React.useEffect(() => {
        // Share with other same-page tabs via context
        setSharedBannerData(previewData);

        // Send to external preview window via BroadcastChannel
        if (broadcastChannel) {
            try {
                broadcastChannel.postMessage(previewData);
            } catch (_) {
                /* channel closed */
            }
        }

        // Persist for external window initial load
        sessionStorage.setItem(
            "scanToPreviewData",
            JSON.stringify(previewData)
        );
    }, [previewData, broadcastChannel, setSharedBannerData]);

    // Open preview in new window.
    const handleOpenInNewWindow = () => {
        // Re-write the latest snapshot before opening so the popup can read
        // it on first paint even if the user clicks before the effect above
        // has flushed (e.g. on the very first interaction after mount).
        sessionStorage.setItem(
            "scanToPreviewData",
            JSON.stringify(previewData)
        );

        // Open new window with tablet dimensions (iPad portrait)
        const width = 768;
        const height = 1024;
        const left = (window.screen.width - width) / 2;
        const top = (window.screen.height - height) / 2;

        // `popup=yes` is what modern Chrome / Firefox / Edge key off to open
        // a real popup window — without it they ignore width/height and just
        // open a new tab the size of the host site.
        window.open(
            "/scan-to-preview",
            "ScanToPreview",
            `popup=yes,width=${width},height=${height},left=${left},top=${top},resizable=yes,scrollbars=yes`
        );
    };

    return (
        <Container data-testid="scan-to-banner-container">
            <LeftSection data-testid="scan-to-banner-left-section">
                <SectionHeader data-testid="scan-to-banner-header">
                    <SectionTitle data-testid="scan-to-banner-title">
                        {t("Banner")}
                    </SectionTitle>
                    <ToggleContainer data-testid="scan-to-banner-toggle-container">
                        <ToggleLabel>{t("Disable banner")}</ToggleLabel>
                        <Toggle
                            data-testid="scan-to-banner-toggle-enabled"
                            isActive={bannerEnabled}
                            mainColor={mainColor}
                            onClick={() => setBannerEnabled(!bannerEnabled)}
                            aria-label={t("Disable banner")}
                        />
                    </ToggleContainer>
                </SectionHeader>

                <ImageTable
                    mainColor={mainColor}
                    displayType={displayType}
                    onDisplayTypeChange={setDisplayType}
                    currentImages={currentImages}
                    uploadedImages={uploadedImages}
                    onDragEnd={handleDragEnd}
                    onOrderChange={handleOrderChange}
                    onToggleActive={toggleImageActive}
                    onViewImage={handleViewImage}
                    onDeleteClick={handleDeleteClick}
                    onAddClick={handleAddButtonClick}
                    fileInputRef={fileInputRef}
                    onImageUpload={handleImageUpload}
                />

                <TitleDescription
                    mainColor={mainColor}
                    selectedLanguage={selectedLanguage}
                    onLanguageChange={setSelectedLanguage}
                    languages={languages}
                    defaultTranslations={defaultTranslations}
                    customTexts={customTexts}
                    onCustomTitleChange={handleCustomTitleChange}
                    onCustomDescriptionChange={handleCustomDescriptionChange}
                    titleVisible={titleVisible}
                    onTitleVisibleChange={() => setTitleVisible(!titleVisible)}
                    descriptionVisible={descriptionVisible}
                    onDescriptionVisibleChange={() =>
                        setDescriptionVisible(!descriptionVisible)
                    }
                    titleColor={titleColor}
                    onTitleColorChange={setTitleColor}
                    descriptionColor={descriptionColor}
                    onDescriptionColorChange={setDescriptionColor}
                    onResetTitle={resetTitle}
                    onResetDescription={resetDescription}
                    onResetAll={resetAll}
                />
            </LeftSection>

            <PreviewToggleButton
                data-testid="scan-to-banner-preview-toggle"
                mainColor={mainColor}
                onClick={() => setPreviewVisible(!previewVisible)}
                title={
                    previewVisible ? "Masquer l'aperçu" : "Afficher l'aperçu"
                }
            >
                {previewVisible ? (
                    <svg viewBox="0 0 24 24" fill="currentColor">
                        <path d="M15.41 7.41L14 6l-6 6 6 6 1.41-1.41L10.83 12z" />
                    </svg>
                ) : (
                    <svg viewBox="0 0 24 24" fill="currentColor">
                        <path d="M10 6L8.59 7.41 13.17 12l-4.58 4.59L10 18l6-6z" />
                    </svg>
                )}
            </PreviewToggleButton>

            <PreviewOverlay
                data-testid="scan-to-banner-preview-overlay"
                isVisible={previewVisible}
                onClick={() => setPreviewVisible(false)}
            />

            <RightSection
                data-testid="scan-to-banner-right-section"
                isVisible={previewVisible}
            >
                <ClosePreviewButton
                    data-testid="scan-to-banner-close-preview"
                    onClick={() => setPreviewVisible(false)}
                    title="Fermer l'aperçu"
                >
                    <svg viewBox="0 0 24 24" fill="currentColor">
                        <path d="M19 6.41L17.59 5 12 10.59 6.41 5 5 6.41 10.59 12 5 17.59 6.41 19 12 13.41 17.59 19 19 17.59 13.41 12z" />
                    </svg>
                </ClosePreviewButton>
                <OpenInNewWindowButton
                    data-testid="scan-to-banner-open-new-window"
                    mainColor={mainColor}
                    onClick={handleOpenInNewWindow}
                    title={t("Open in new window")}
                >
                    <svg viewBox="0 0 24 24" fill="currentColor">
                        <path d="M19 19H5V5h7V3H5c-1.11 0-2 .9-2 2v14c0 1.1.89 2 2 2h14c1.1 0 2-.9 2-2v-7h-2v7zM14 3v2h3.59l-9.83 9.83 1.41 1.41L19 6.41V10h2V3h-7z" />
                    </svg>
                </OpenInNewWindowButton>
                <ScanToMobilePreview
                    data-testid="scan-to-banner-mobile-preview"
                    mainColor={mainColor}
                    selectedLanguage={selectedLanguage}
                    bannerData={{
                        bannerEnabled,
                        // BannerSection picks the active language from
                        // bannerData.selectedLanguage (its OWN picker) so
                        // it doesn't follow the active rubric's locale.
                        // Without this field the preview falls back to
                        // the project default ("fr") and the user's
                        // Italian/Spanish/etc. edits never appear.
                        selectedLanguage,
                        activeImages,
                        customTexts,
                        titleVisible,
                        descriptionVisible,
                        titleColor,
                        descriptionColor,
                        currentSlide,
                    }}
                    offresData={sharedOffresData as OffresPreviewData | null}
                    actionsData={sharedActionsData as ActionsPreviewData | null}
                    publicitesData={
                        sharedPublicitesData as PublicitesPreviewData | null
                    }
                    commandeData={
                        sharedCommandeData as CommandePreviewData | null
                    }
                    diversData={sharedDiversData as DiversPreviewData | null}
                    paiementData={sharedPaiementData}
                    appelServeurData={sharedAppelServeurData}
                />
            </RightSection>

            {/* Delete Confirmation Modal */}
            {deleteConfirmOpen && (
                <DeleteConfirmModal
                    mainColor={mainColor}
                    onCancel={cancelDelete}
                    onConfirm={confirmDelete}
                />
            )}
        </Container>
    );
}
